// ── cohort-progress-collection: week-wise grid adapter for progress tab ─────

import { useMemo } from "react";
import { useCollection, type CollectionColumnDef, type UseCollectionResult } from "@vjal/ui";

import type { CoachingWeekWiseProgressDto } from "../../../services/coaching-z6-dtos";
import { COHORT_TAB_PAGE_SIZE } from "./cohort-tab-collection-shared";

export type ProgressRow = { id: string; participant: string; completion: string } & Record<string, unknown>;

function shortStatus(status: string): string {
  if (status === "attended") return "A";
  if (status === "missed") return "M";
  if (status === "excused") return "E";
  if (status === "submitted" || status === "reviewed") return "S";
  return "·";
}

export function mapProgressParticipants(progress: CoachingWeekWiseProgressDto["progress"]): ProgressRow[] {
  const weeks = progress?.weeks ?? [];
  return (progress?.participants ?? []).map((participant) => {
    const cellByWeek = new Map(participant.cells.map((cell) => [cell.weekNumber, cell]));
    const row: ProgressRow = {
      id: participant.participantId,
      participant: participant.participantName ?? participant.email,
      completion: `${participant.completionPercent}%`
    };
    for (const week of weeks) {
      const cell = cellByWeek.get(week.weekNumber);
      row[`w${week.weekNumber}`] = cell
        ? `${shortStatus(cell.attendanceStatus)}/${shortStatus(cell.assignmentStatus)}${cell.reportAvailable ? "/R" : ""}`
        : "—";
    }
    return row;
  });
}

export function buildProgressCollectionColumns(
  weeks: Array<{ weekNumber: number }>
): CollectionColumnDef<ProgressRow>[] {
  const columns: CollectionColumnDef<ProgressRow>[] = [
    {
      id: "participant",
      header: "Participant",
      searchValue: (row) => String(row.participant ?? ""),
      priority: 1,
      cell: (row) => String(row.participant)
    },
    { id: "completion", header: "Completion", priority: 2, cell: (row) => String(row.completion) }
  ];
  for (const week of weeks) {
    columns.push({
      id: `w${week.weekNumber}`,
      header: `W${week.weekNumber}`,
      priority: 3,
      cell: (row) => String(row[`w${week.weekNumber}`] ?? "—")
    });
  }
  return columns;
}

export function useCohortProgressCollection(
  rows: ProgressRow[],
  weeks: Array<{ weekNumber: number }>
): UseCollectionResult<ProgressRow> {
  const columns = useMemo(() => buildProgressCollectionColumns(weeks), [weeks]);

  return useCollection<ProgressRow>({
    mode: "client",
    rows,
    columns,
    pageSize: COHORT_TAB_PAGE_SIZE,
    urlSync: false,
    getRowKey: (row) => row.id
  });
}
